import { createFormatForDescription as cffd } from '../createFormatForDescription'
import { Explanation, explain, explainOptional } from '../explain/Explanation'
import PaymentChannelCreateInstructions from '../types/instructions/PaymentChannelCreateInstructions'
import TransactionType from '../types/TransactionType'
import { parseDate } from '../util'

/**
 * Annotate PaymentChannelCreate transactions.
 *
 * @property type - The transaction type.
 * @property amount - An explanation of the amount.
 * @property cancelAfter - An explanation of the cancelAfter.
 * @property destination - An explanation of the destination.
 * @property destinationTag - An explanation of the destinationTag.
 * @property publicKey - An explanation of the publicKey.
 * @property settleDelay - An explanation of the settleDelay.
 */
export interface PaymentChannelCreateAnnotation {
  readonly type: TransactionType.PaymentChannelCreate

  readonly amount?: Explanation<string>
  readonly cancelAfter?: Explanation<number>
  readonly destination: Explanation<string>
  readonly destinationTag?: Explanation<number>
  readonly publicKey: Explanation<string>
  readonly settleDelay: Explanation<number>
}

/**
 * Takes a transaction object and returns an annotated transaction object.
 * The returned object contains explanations about the various transaction features.
 *
 * @param transactionInstructions - The input transaction.
 * @returns An annotated transaction object detailing the various transaction features.
 */
export function annotate(
  transactionInstructions: PaymentChannelCreateInstructions,
): PaymentChannelCreateAnnotation {
  const amount = explain(
    transactionInstructions.Amount,
    `${cffd(
      transactionInstructions.Amount,
      'amount',
    )} XRP (in drops) can be dispensed by the channel, including XRP previously redeemed.`,
  )

  const cancelAfter = explainOptional(
    transactionInstructions.CancelAfter,
    (date) => `This channel expires on ${cffd(parseDate(date), 'cancelAfter')}`,
  )

  const destination = explain(
    transactionInstructions.Destination,
    `${cffd(
      transactionInstructions.Destination,
      'destination',
    )} will receive XRP claims against this channel.`,
  )

  const publicKey = explain(
    transactionInstructions.PublicKey,
    `${cffd(
      transactionInstructions.PublicKey,
      'publicKey',
    )} is the public key of the key pair the source will use to sign claims against this channel, in hexadecimal.`,
  )

  const destinationTag = explainOptional(
    transactionInstructions.DestinationTag,
    (tag) =>
      `This tag identifies a hosted recipient or other information for the recipient of the deleted account's leftover XRP: ${cffd(
        tag.toString(),
        'destinationTag',
      )}`,
  )

  const settleDelay = explain(
    transactionInstructions.SettleDelay,
    `The source address must wait ${cffd(
      transactionInstructions.SettleDelay.toString(),
      'settleDelay',
    )} seconds before closing the channel if it has unclaimed XRP.`,
  )

  return {
    type: TransactionType.PaymentChannelCreate,
    amount,
    ...(cancelAfter && { cancelAfter }),
    destination,
    ...(destinationTag && { destinationTag }),
    publicKey,
    settleDelay,
  }
}
